/**
 * FDDD (Failure Data Deep Dive) Module Constants
 *
 * Defines the 16 analysis dimensions, their groupings, and common deep-dive paths.
 * NOTE: Dimension key names (used in API requests) are subject to change.
 *       Update `DimensionKeyMap` values when BE confirms final naming.
 */

// ── Dimension Key Map ─────────────────────────────────────────────────
// Maps each dimension to the key used in API request/response.
// The values here correspond to the request parameter field names and the
// `dimension` field returned in FailureRateNode.
export const DimensionKeyMap = {
  // Group 1: Build Identifiers (Dimensions 1-6)
  PROJECT: "project",
  PHASE_BUILD: "phaseBuild",
  CM_LOCATION: "cmLocation",

  CONFIG: "config",
  BUILD_MATRIX: "buildMatrix", // ⚠️ TODO on BE — table not created
  DEVICE: "device",

  // Special: Full identifier tree (not a flat dimension — used for IdentifierMultiSelect)
  IDENTIFIER_TREE: "identifierTree",

  // Group 2: Test Information (Dimensions 7-10)
  TEST: "test",
  TEST_SEQUENCE: "testSequence",
  READOUT: "readout",
  TEST_VARIABLE: "testVariable", // ⚠️ TODO on BE — table mapping not defined

  // Group 3: Issue Information (Dimensions 11-16)
  ISSUE: "issue",
  FAILED_PART: "failedPart",
  RECOVERY_TYPE: "recoveryType",
  ISSUE_SEVERITY: "issueSeverity", // ⚠️ TODO on BE — field mapping not defined
  FAILURE_CATEGORY: "failureCategory",
  ISSUE_FOUND_DATE: "issueFoundDate",
} as const;

export type TDimensionValue = (typeof DimensionKeyMap)[keyof typeof DimensionKeyMap];
export type TDimensionKey = keyof typeof DimensionKeyMap;

// ── Dimension Metadata ────────────────────────────────────────────────
// Static info about each dimension for rendering purposes.
//
// A dimension's role in the two FDDD surfaces is captured by two INDEPENDENT
// capability flags — do NOT infer one from `isMandatory`:
//   • isPathEligible — can be dragged into the Analysis Path (grouping key sent
//     to get_failure_rate_summary). Combined "phaseBuild" is path-eligible even
//     though it is also the mandatory scope selector.
//   • isFilterable   — can be pre-filtered in the DataSetSelector panel (has a
//     DimensionType option list from get_cascading_filter_options). "phaseBuild"
//     and "project" are NOT filterable; "issueFoundDate" is filterable but not
//     path-eligible (it is a date-range filter, never a grouping level).
// `isMandatory` now means ONLY "required scope selection" (project + phase-build).
export interface IDimensionMeta {
  key: TDimensionValue;
  labelKey: string; // i18n key for display name
  group: "buildIdentifier" | "testInfo" | "issueInfo";
  isMandatory: boolean; // required scope selection (project + phase-build)
  isTodo: boolean; // true if BE hasn't implemented this dimension yet
  isPathEligible: boolean; // can be a grouping level in the Analysis Path
  isFilterable: boolean; // has cascading pre-filter options in DataSetSelector
  hasUnifiedSearch: boolean; // whether this dimension participates in the unified search box
  searchFields: string[]; // DB fields searched for this dimension
}

export const DimensionMetaList: IDimensionMeta[] = [
  // Group 1: Build Identifiers
  {
    key: DimensionKeyMap.PROJECT,
    labelKey: "fddd.dimensions.project",
    group: "buildIdentifier",
    isMandatory: true,
    isTodo: false,
    isPathEligible: false,
    isFilterable: false,
    hasUnifiedSearch: false,
    searchFields: [],
  },
  {
    // Combined Phase-Build. Mandatory scope selector AND a path grouping level
    // (Asana 1214773896590594): BE get_failure_rate_summary groups by
    // phase_id||'/'||build_id for a single "phaseBuild" path key. Not filterable
    // — the cascading-filter endpoint has no phaseBuild DimensionType.
    key: DimensionKeyMap.PHASE_BUILD,
    labelKey: "fddd.dimensions.phaseBuild",
    group: "buildIdentifier",
    isMandatory: true,
    isTodo: false,
    isPathEligible: true,
    isFilterable: false,
    hasUnifiedSearch: false,
    searchFields: [],
  },
  // {
  //   key: DimensionKeyMap.CM_LOCATION,
  //   labelKey: "fddd.dimensions.cmLocation",
  //   group: "buildIdentifier",
  //   isMandatory: false,
  //   isTodo: false,
  //   isPathEligible: true,
  //   isFilterable: true,
  //   hasUnifiedSearch: true,
  //   searchFields: ["name"],
  // },
  {
    key: DimensionKeyMap.CONFIG,
    labelKey: "fddd.dimensions.config",
    group: "buildIdentifier",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["config_name"],
  },
  // Build Matrix — FE feature not finished yet; hidden from the palette and
  // pre-filters until ready. Re-enable this meta entry (and the buildMatrix
  // preset / CommonPath below) when the feature lands.
  // {
  //   key: DimensionKeyMap.BUILD_MATRIX,
  //   labelKey: "fddd.dimensions.buildMatrix",
  //   group: "buildIdentifier",
  //   isMandatory: false,
  //   isTodo: false,
  //   isPathEligible: true,
  //   isFilterable: true,
  //   hasUnifiedSearch: true,
  //   searchFields: ["short_id"],
  // },
  {
    key: DimensionKeyMap.DEVICE,
    labelKey: "fddd.dimensions.device",
    group: "buildIdentifier",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["dsn", "real_dsn"],
  },

  // Group 2: Test Information
  {
    key: DimensionKeyMap.TEST,
    labelKey: "fddd.dimensions.test",
    group: "testInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["test_name", "testcode"],
  },
  {
    key: DimensionKeyMap.TEST_SEQUENCE,
    labelKey: "fddd.dimensions.testSequence",
    group: "testInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["grouping_sequence"],
  },
  {
    key: DimensionKeyMap.READOUT,
    labelKey: "fddd.dimensions.readout",
    group: "testInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["order"],
  },
  {
    key: DimensionKeyMap.TEST_VARIABLE,
    labelKey: "fddd.dimensions.testVariable",
    group: "testInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: [],
  },

  // Group 3: Issue Information
  {
    key: DimensionKeyMap.ISSUE,
    labelKey: "fddd.dimensions.issue",
    group: "issueInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["failure_mode"],
  },
  {
    key: DimensionKeyMap.FAILED_PART,
    labelKey: "fddd.dimensions.failedPart",
    group: "issueInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["failing_component"],
  },
  {
    key: DimensionKeyMap.RECOVERY_TYPE,
    labelKey: "fddd.dimensions.recoveryType",
    group: "issueInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["recovery_steps"],
  },
  {
    key: DimensionKeyMap.ISSUE_SEVERITY,
    labelKey: "fddd.dimensions.issueSeverity",
    group: "issueInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: false,
    searchFields: [],
  },
  {
    key: DimensionKeyMap.FAILURE_CATEGORY,
    labelKey: "fddd.dimensions.failureCategory",
    group: "issueInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: true,
    isFilterable: true,
    hasUnifiedSearch: true,
    searchFields: ["category"],
  },
  {
    // Issue Found Date is a date-range pre-filter, never a grouping level.
    key: DimensionKeyMap.ISSUE_FOUND_DATE,
    labelKey: "fddd.dimensions.issueFoundDate",
    group: "issueInfo",
    isMandatory: false,
    isTodo: false,
    isPathEligible: false,
    isFilterable: true,
    hasUnifiedSearch: false,
    searchFields: [],
  },
];

// Quick lookups
export const MandatoryDimensions = DimensionMetaList.filter(d => d.isMandatory);
export const OptionalDimensions = DimensionMetaList.filter(d => !d.isMandatory);
export const TodoDimensions = DimensionMetaList.filter(d => d.isTodo);

// ── Default (built-in) presets for FDDD — these are FE-only, never sent to or stored by the backend ──
import { IPresetItem } from "@type/axios";

export const FDDD_DEFAULT_PRESETS: IPresetItem[] = [
  { uuid: "default_1", path: ["test", "issue"], description: "Evaluate test performance and identify failure causes", isDefault: true },
  { uuid: "default_2", path: ["issue", "test"], description: "Track where specific issues appear across tests", isDefault: true },
  // Re-enable once the Build Matrix feature is finished (see BUILD_MATRIX meta above).
  // { uuid: "default_3", path: ["test", "buildMatrix", "issue"], description: "Find which suppliers cause higher failure rates", isDefault: true },
];
export const AvailableOptionalDimensions = OptionalDimensions.filter(d => !d.isTodo);

// Dimensions that can appear in the Deep Dive Steps path (Analysis Path palette).
// Keyed on isPathEligible so path-membership is independent of scope/filter roles —
// e.g. the mandatory combined phaseBuild is included, the date filter is not.
export const DeepDiveEligibleDimensions = DimensionMetaList.filter(d => d.isPathEligible && !d.isTodo);

// Dimensions offered as cascading pre-filters in the DataSetSelector panel.
export const PreFilterDimensions = DimensionMetaList.filter(d => d.isFilterable && !d.isTodo);

// ── Common Deep-Dive Paths ────────────────────────────────────────────
// Pre-defined popular analysis paths shown as quick-select buttons
export const CommonPaths = {
  TEST_ISSUE: {
    labelKey: "fddd.commonPaths.testIssue",
    path: [DimensionKeyMap.TEST, DimensionKeyMap.ISSUE] as TDimensionValue[],
  },
  ISSUE_TEST: {
    labelKey: "fddd.commonPaths.issueTest",
    path: [DimensionKeyMap.ISSUE, DimensionKeyMap.TEST] as TDimensionValue[],
  },
  // Re-enable once the Build Matrix feature is finished (see BUILD_MATRIX meta above).
  // TEST_BUILD_MATRIX_ISSUE: {
  //   labelKey: "fddd.commonPaths.testBuildMatrixIssue",
  //   path: [DimensionKeyMap.TEST, DimensionKeyMap.BUILD_MATRIX, DimensionKeyMap.ISSUE] as TDimensionValue[],
  // },
} as const;

export type TCommonPathKey = keyof typeof CommonPaths;
